import { questionAPI, phcAPI } from '../utils/api';
import { saveQuestionsOffline, savePhcsOffline } from '../utils/offlineStorage';
import { setCachedData, getCachedData } from '../hooks/useOfflineFirstData';

// Cache configuration (align with offline-first hook: 7 days)
const PRELOAD_CONFIG = {
  QUESTIONS_CACHE_TTL: 7 * 24 * 60 * 60 * 1000,
  PHCS_CACHE_TTL: 7 * 24 * 60 * 60 * 1000,
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000 // 1 second
};

// User types for questions
const USER_TYPES = ['community', 'health-worker', 'wdc'];

class DataPreloadService {
  constructor() {
    this.isPreloading = false;
    this.preloadPromise = null;
    this.lastPreloadTime = null;
    this.preloadCallbacks = [];
  }

  // Retry wrapper for API calls
  async withRetry(fn, maxAttempts = PRELOAD_CONFIG.MAX_RETRY_ATTEMPTS) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        // ...existing code...
        
        if (attempt < maxAttempts) {
          // Exponential backoff
          const delay = PRELOAD_CONFIG.RETRY_DELAY * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  }

  // Check if data needs to be preloaded
  needsPreload() {
    const phcsCached = getCachedData('phcs', PRELOAD_CONFIG.PHCS_CACHE_TTL);
    const missingPhcs = !phcsCached;

    const missingQuestions = USER_TYPES.some(userType => {
      const cacheKey = `questions_${userType}`;
      return !getCachedData(cacheKey, PRELOAD_CONFIG.QUESTIONS_CACHE_TTL);
    });

    return missingPhcs || missingQuestions;
  }

  // Preload PHCs data
  async preloadPHCs() {
    try {
      const cached = getCachedData('phcs', PRELOAD_CONFIG.PHCS_CACHE_TTL);
      if (cached) {
        return cached;
      }

      const response = await this.withRetry(() => phcAPI.getPHCs());
      const phcList = response.data;
      
      // Save to offline storage
      await savePhcsOffline(phcList);
      
      // Cache using the offline-first system
      setCachedData('phcs', phcList);
      
      // ...existing code...
      return phcList;
    } catch (error) {
      // ...existing code...
      throw error;
    }
  }

  // Preload questions for a specific user type
  async preloadQuestions(userType) {
    try {
      // ...existing code...
      
      const categoryMap = {
        'community': 1,
        'health-worker': 2,
        'wdc': 3
      };

      const categoryId = categoryMap[userType.toLowerCase()];
      if (!categoryId) {
        throw new Error(`Invalid user type: ${userType}`);
      }

      const cacheKey = `questions_${userType}`;
      const cached = getCachedData(cacheKey, PRELOAD_CONFIG.QUESTIONS_CACHE_TTL);
      if (cached) {
        return cached;
      }

      const response = await this.withRetry(() => questionAPI.getQuestions({ category: categoryId }));
      const questionsData = response.data;
      
      // Save to offline storage
      await saveQuestionsOffline(questionsData, userType);
      
      // Cache using the offline-first system
      setCachedData(cacheKey, questionsData);
      
      // ...existing code...
      return questionsData;
    } catch (error) {
      // ...existing code...
      throw error;
    }
  }

  // Preload all critical data
  async preloadAllData() {
    if (this.isPreloading && this.preloadPromise) {
      return this.preloadPromise;
    }

    this.isPreloading = true;
    
    this.preloadPromise = this._performPreload()
      .finally(() => {
        this.isPreloading = false;
        this.lastPreloadTime = new Date();
        
        // Notify all callbacks
        this.preloadCallbacks.forEach(callback => {
          try {
            callback();
          } catch (error) {
            // ...existing code...
          }
        });
        
        // Clear callbacks
        this.preloadCallbacks = [];
      });

    return this.preloadPromise;
  }

  // Internal method to perform the actual preloading
  async _performPreload() {
    const startTime = Date.now();
    // ...existing code...
    
    try {
      // Preload PHCs in parallel with questions
      const preloadPromises = [
        this.preloadPHCs(),
        ...USER_TYPES.map(userType => this.preloadQuestions(userType))
      ];

      // Wait for all preloading to complete
      const results = await Promise.allSettled(preloadPromises);
      
      // Log results
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;
      
      console.log(`[DataPreloadService] Preloading completed: ${successful} successful, ${failed} failed`);
      console.log(`[DataPreloadService] Total preloading time: ${Date.now() - startTime}ms`);
      
      // If any failed, throw an error to indicate partial failure
      if (failed > 0) {
        const errors = results
          .filter(result => result.status === 'rejected')
          .map(result => result.reason);
        throw new Error(`Preloading partially failed: ${errors.map(e => e.message).join(', ')}`);
      }
      
      return {
        success: true,
        duration: Date.now() - startTime,
        results: results.map(result => result.status === 'fulfilled' ? result.value : null)
      };
    } catch (error) {
      // ...existing code...
      throw error;
    }
  }

  // Add a callback to be notified when preloading completes
  onPreloadComplete(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    
    this.preloadCallbacks.push(callback);
  }

  // Get preloading status
  getPreloadStatus() {
    return {
      isPreloading: this.isPreloading,
      lastPreloadTime: this.lastPreloadTime,
      needsPreload: this.needsPreload()
    };
  }

  // Force refresh all cached data
  async forceRefreshAll() {
    console.log('[DataPreloadService] Force refreshing all cached data...');
    
    // Clear existing cache
    USER_TYPES.forEach(userType => {
      const cacheKey = `questions_${userType}`;
      localStorage.removeItem(`offline_cache_${cacheKey}`);
    });
    localStorage.removeItem(`offline_cache_phcs`);
    
    // Preload fresh data
    return this.preloadAllData();
  }

  // Get cached data status
  getCacheStatus() {
    const status = {
      phcs: getCachedData('phcs', PRELOAD_CONFIG.PHCS_CACHE_TTL) ? 'cached' : 'not_cached',
      questions: {}
    };

    USER_TYPES.forEach(userType => {
      const cacheKey = `questions_${userType}`;
      status.questions[userType] = getCachedData(cacheKey, PRELOAD_CONFIG.QUESTIONS_CACHE_TTL) ? 'cached' : 'not_cached';
    });

    return status;
  }
}

// Create singleton instance
const dataPreloadService = new DataPreloadService();

export default dataPreloadService;